import moment from "moment";
import React, { useEffect } from "react";
import FadeLoader from "react-spinners/FadeLoader";

import { useDispatch, useSelector } from "react-redux";
import { initList, fetchNews, setCurrentPage } from "store/slices/newsSlice";

import "styles/component.scss";

const Pagination = ({ lang, categoryKey, currentPage, totalPages, onPageChange }) => {
  const dispatch = useDispatch();

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxButtons = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();
  const noNewsMessage = {
    en: "No news available",
    es: "No hay noticias disponibles",
    fr: "Aucune actualitAc disponible",
    de: "Keine Neuigkeiten verfA¬gbar",
    glossary: "No glossary terms available",
  };

  return totalPages > 0 ? (
    <nav aria-label="Page navigation">
      <ul className="pagination">
        {/* First button */}
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <a
            className="page-link"
            onClick={() => dispatch(onPageChange({ lang, page: 1, category: categoryKey }))}
            disabled={currentPage === 1}
          >
            &laquo;
          </a>
        </li>

        {/* Dynamic page buttons */}
        {pageNumbers.map((page) => (
          <li
            key={page}
            className={`page-item ${currentPage === page ? "active" : ""}`}
          >
            <a
              className="page-link"
              onClick={() => dispatch(onPageChange({ lang, page, category: categoryKey }))}
              style={{
                backgroundColor: currentPage === page ? "#f8cf3a" : "#dedede",
                borderColor: currentPage === page ? "#f8cf3a" : "#dedede",
              }}
            >
              {page}
            </a>
          </li>
        ))}

        {/* Last button */}
        <li
          className={`page-item ${
            currentPage === totalPages ? "disabled" : ""
          }`}
        >
          <a
            className="page-link"
            onClick={() => dispatch(onPageChange({ lang, page: totalPages, category: categoryKey }))}
            disabled={currentPage === totalPages}
          >
            &raquo;
          </a>
        </li>
      </ul>
    </nav>
  ) : (
    <h3 className="text-xl text-gray-400 mb-2">{noNewsMessage[lang]}</h3>
  );
};

const HeadlessNews = ({
  isCompact,
  isLandscape = false,
  lang = "en",
  categorySlug,
  categoryLabel,
  primaryCategoryLabel,
  useWpPrimaryCategory = false,
  hideCategoryChip = false,
  searchTerm = "",
  itemsToShow = 3,
  showExcerptTooltip = false,
  enableFlip = false,
  perPage,
}) => {
  const dispatch = useDispatch();
  const categoryKey = categorySlug || "all";
  const listKey = searchTerm ? `${categoryKey}::search=${searchTerm}` : categoryKey;

  const {
    list,
    currentPage: currentPages,
    totalPages,
    loading,
    error,
  } = useSelector((state) => state.news);

  const currentPage = currentPages?.[lang]?.[listKey] || 1;

  const itemColClass = isLandscape
    ? "col-span-12 px-4"
    : "col-span-12 lg:col-span-4 md:col-span-6 px-4";

  useEffect(() => {
    dispatch(initList({ lang, category: listKey }));
    dispatch(setCurrentPage({ lang, category: listKey, page: 1 }));
  }, [dispatch, lang, listKey]);

  useEffect(() => {
    dispatch(
      fetchNews({
        lang,
        page: currentPage,
        categorySlug: categorySlug || undefined,
        perPage,
        searchTerm,
        listKey,
      })
    );
  }, [dispatch, lang, currentPage, listKey, categorySlug, perPage, searchTerm]);

  const htmlToText = (html) => {
    const temp = document.createElement("div");
    temp.style.display = "none";
    temp.innerHTML = html;

    return temp.textContent;
  };

  const stripHtml = (html) => {
    if (!html) return "";
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  const cleanCategoryLabel = (value) =>
    stripHtml(value).replace(/^[^A-Za-z0-9]+/, "").trim();

  const normalizeCategory = (value) => cleanCategoryLabel(value).toLowerCase();

  const getPrimaryCategoryFromYoast = (item) => {
    const yoast = item?.yoast_head_json;
    const fallback =
      item?.primary_category ||
      item?.primary_category_name ||
      item?.yoast_primary_category;
    if (!yoast) return stripHtml(fallback || "");

    const graph = yoast?.schema?.["@graph"];
    const graphArticle = Array.isArray(graph)
      ? graph.find((entry) => entry?.["@type"] === "Article")
      : null;
    const articleSection =
      graphArticle?.articleSection || yoast?.schema?.articleSection;
    const normalizedSection = Array.isArray(articleSection)
      ? articleSection[0]
      : articleSection;

    return stripHtml(
      normalizedSection ||
        yoast?.article_section ||
        yoast?.primary_category ||
        fallback
    );
  };

  const getDisplayCategory = (item) => {
    const titles = item?.category_titles || [];
    const wpPrimaryCategory = getPrimaryCategoryFromYoast(item);
    const cleanedPrimaryCategory = cleanCategoryLabel(wpPrimaryCategory);

    if (useWpPrimaryCategory && cleanedPrimaryCategory)
      return cleanedPrimaryCategory;
    if (!titles.length) return cleanCategoryLabel(categoryLabel || "");
    if (!primaryCategoryLabel) return cleanCategoryLabel(titles[0]);

    const match = titles.find(
      (title) => normalizeCategory(title) === normalizeCategory(primaryCategoryLabel)
    );

    return cleanCategoryLabel(match || titles[0]);
  };

  // Generate the appropriate link based on the lang parameter
  const generateItemLink = (item) => {
    if (lang === "glossary") {
      return `/glossary/${item.slug}`;
    } else if (lang === "case_study") {
      return `/customer-success-stories/case-study/${item.slug}`;
    }
    return `/knowledge/news/${lang}/${item.slug}`;
  };

  const getExcerptText = (item) => {
    const excerptText = stripHtml(item?.excerpt?.rendered || "");
    if (!excerptText) return "Learn more about this article.";
    if (excerptText.length <= 180) return excerptText;
    return `${excerptText.slice(0, 180).trim()}...`;
  };

  if (!list || !currentPages || !totalPages) {
    return (
      <div className="w-full h-full flex items-center justify-center py-16">
        <FadeLoader />
      </div>
    );
  }

  if (loading)
    return (
      <div className="w-full h-full flex items-center justify-center py-16">
        <FadeLoader />
      </div>
    );

  const pageList = list?.[lang]?.[listKey]?.[currentPage] || [];
  const visibleList = isCompact ? pageList.slice(0, itemsToShow) : pageList;
  const shouldFlip = enableFlip && !isLandscape;

  return (
    <div className="inews">
      <div className="grid grid-cols-12 lg:px-16 px-4 gap-6">
        {visibleList &&
          visibleList.map((item) => {
            const displayCategory = getDisplayCategory(item);
            return (
              <div className={itemColClass} key={item.id}>
                <a
                  href={generateItemLink(item)}
                  target="_self"
                  className={`news-item-${item.id}`}
                  title={showExcerptTooltip ? htmlToText(item.excerpt.rendered) : undefined}
                >
                  <div
                    className={`mbox ${isLandscape ? "landscape" : ""} ${
                      shouldFlip ? "glossary-flip" : ""
                    }`}
                  >
                    {shouldFlip ? (
                      <div className="glossary-card-inner">
                        <div className="glossary-card-face front">
                          <div
                            className="thumbnail"
                            style={{
                              backgroundImage: `url(${item.custom_thumbnail})`,
                            }}
                          ></div>
                          <div className="box">
                            <div className="chip-row">
                              <h6 disabled>
                                {moment(item.date).format("MMM D, YYYY")}
                              </h6>
                              {!hideCategoryChip && displayCategory ? (
                                <div className="category-chip">
                                  {displayCategory}
                                </div>
                              ) : null}
                            </div>
                            <h4
                              dangerouslySetInnerHTML={{
                                __html: item.title.rendered,
                              }}
                            ></h4>
                            <div className="mt-2">LEARN MORE</div>
                          </div>
                        </div>
                        <div className="glossary-card-face back">
                          <div className="glossary-card-content">
                            <h4
                              dangerouslySetInnerHTML={{
                                __html: item.title.rendered,
                              }}
                            ></h4>
                            <p className="glossary-description">
                              {getExcerptText(item)}
                            </p>
                            <span className="see-more">LEARN MORE</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div
                          className="thumbnail"
                          style={{
                            backgroundImage: `url(${item.custom_thumbnail})`,
                          }}
                        ></div>
                        <div className="box">
                          <div className="chip-row">
                            <h6 disabled>
                              {moment(item.date).format("MMM D, YYYY")}
                            </h6>
                            {!hideCategoryChip && displayCategory ? (
                              <div className="category-chip">
                                {displayCategory}
                              </div>
                            ) : null}
                          </div>
                          <h4
                            dangerouslySetInnerHTML={{
                              __html: item.title.rendered,
                            }}
                          ></h4>
                          <div className="mt-2">LEARN MORE</div>
                        </div>
                      </>
                    )}
                  </div>
                </a>
              </div>
            );
          })}
      </div>
      {!isCompact && (
        <div className="grid grid-cols-12 pagination">
          <Pagination
            lang={lang}
            categoryKey={listKey}
            currentPage={currentPage}
            totalPages={totalPages?.[lang]?.[listKey] || 0}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default HeadlessNews;

